import {
  ColDef,
  NumberFilter,
  ValueFormatterFunc,
  ValueFormatterParams,
  ValueGetterFunc,
  ValueGetterParams,
} from '@ag-grid-community/core'
import { BaseColDefParams } from '@ag-grid-community/core/dist/cjs/entities/colDef'
import { LOCALE } from '@helios/ag-grid-config'
import {
  ClearwaterTransactionLabelParts,
  transactionLinkId,
} from '@helios/comments'
import { fromMetaStringToNumber } from '@helios/utils'
import { composeFieldPath } from '@northfork/basics'
import { Break } from '../model/break.model'

export type MetaDataColType = 'identifier' | 'number'

const field = composeFieldPath([] as Break[])

/**
 * Project metadata from column definition params.
 * Used by various getter functions.
 */
function getMetaData(params: BaseColDefParams) {
  return params.data?.metaData ?? {}
}

const TRANSACTION_ID_KEY = 'Transaction Id'
const TRANSACTION_ORIG_ID_KEY = 'origTranId'

function transactionIdFromMetaData(metaData: Record<string, unknown>) {
  const value = metaData[TRANSACTION_ID_KEY]
  if (value === null) return null
  return value ?? metaData[TRANSACTION_ORIG_ID_KEY]
}

export function breakOrigTranIdFromMetaData(
  brk: Pick<Break, 'metaData'>,
): string | undefined {
  return transactionLinkId(
    (brk.metaData ?? {})[TRANSACTION_ORIG_ID_KEY] as
      | string
      | number
      | null
      | undefined,
  )
}

export function breakTransactionLabelPartsFromMetaData(
  brk: Pick<Break, 'metaData' | 'securityIdentifier'>,
): ClearwaterTransactionLabelParts {
  return {
    tranTypeCode: (brk.metaData ?? {})['Tran Type'] as string | undefined,
    amount: undefined,
    units: undefined,
    entryDate: undefined,
    tradeDate: undefined,
    secIdentifier: brk.securityIdentifier,
  }
}

export const getMetaDataNumber = (
  value: number | string | undefined,
): number | undefined => {
  if (typeof value === 'string') {
    const num = Number.parseFloat(value)
    if (Math.abs(num) < 1) {
      return Number(num)
    }
    return Number(num)
  }
  if (typeof value === 'number') {
    if (Math.abs(value) < 1) {
      return Number(value)
    }
    return Number(value)
  }

  return value
}

export const formatMetaDataNumber = (value: unknown): string | undefined => {
  if (!isNaN(Number(value))) {
    const num = Number(value)
    if (Math.abs(num) < 1) {
      return num.toFixed(8)
    }
    return Number(value).toLocaleString(LOCALE)
  }

  return undefined
}

/**
 * Create value getter for a metadata column
 */
function createMetaDataValueGetter(
  name: string,
  type?: MetaDataColType,
): ValueGetterFunc {
  return params => {
    const metaData = getMetaData(params)
    let value
    if (name === 'Transaction ID') value = transactionIdFromMetaData(metaData)
    else if (name === 'Basis ID') value = metaData['Basis Id']
    else value = metaData[name]
    if (value === null) return value
    switch (type) {
      case 'identifier':
        return value
      case 'number': {
        const num = getMetaDataNumber(value)
        return num
      }
      default:
        return value
    }
  }
}

/**
 * Create value formatter for a metadata column
 */
function createMetaDataValueFormatter(
  name: string,
  type?: MetaDataColType,
): ValueFormatterFunc {
  return params => {
    const value = getMetaData(params)?.[name]
    if (value === null) return value
    switch (type) {
      case 'identifier':
        return value
      case 'number':
        return formatMetaDataNumber(value)
      default:
        return value
    }
  }
}

/**
 * Generate Header Name for the fields which have different keyName in response data
 */
function generateHeaderName(name: string): string {
  if (name === 'Left Source') return 'Comparison Field'
  else if (name === 'Left Value') return 'Comparison Value'
  else if (name === 'Right Source') return 'Base Field'
  else if (name === 'Right Value') return 'Base Value'
  else return name
}

export const generateMetaDataColumn = (
  colId: string,
  name: string,
  type?: 'identifier' | 'number',
): ColDef => {
  return {
    colId,
    field: field('metaData'),
    headerName: generateHeaderName(name),
    hide: true,
    valueGetter: createMetaDataValueGetter(name, type),
    valueFormatter: createMetaDataValueFormatter(name, type),
    filter: type === 'number' ? NumberFilter : undefined,
  }
}
export const generateMetaDataColumnForNumber = (
  colId: string,
  name: string,
  type?: 'identifier' | 'number',
): ColDef => {
  return {
    colId,
    field: field('metaData'),
    headerName: name,
    valueFormatter: params =>
      params.data.isHplAccount
        ? formatNumber(params, 4)
        : formatNumber(params, 2),
    hide: true,
    valueGetter: createMetaDataValueGetter(name, type),
    filter: type === 'number' ? NumberFilter : undefined,
  }
}

/**
 * Calculate difference when either CWAN Value or Client Value (External Value) is null
 */
export const calculateDifference = (
  metaData: Record<string, number>,
): number | string | undefined => {
  // difference = client value - prediction
  if (metaData['Predicted Value'] && metaData['Difference']) {
    return metaData['Difference']
  }

  // difference = client value - cwan value
  if (metaData['Client Value'] == null && metaData['CWAN Value']) {
    return -metaData['CWAN Value']
  }
  if (metaData['CWAN Value'] == null && metaData['Client Value']) {
    return metaData['Client Value']
  }

  if (metaData['Difference']) {
    return metaData['Difference']
  }

  return
}

export const differenceValueGetter: ValueGetterFunc = (
  params: ValueGetterParams,
) => {
  const diff = calculateDifference(getMetaData(params))
  return getMetaDataNumber(diff)
}

export const differenceValueFormatter: ValueFormatterFunc = (
  params: ValueFormatterParams,
): string => {
  const diff = calculateDifference(getMetaData(params))
  return formatMetaDataNumber(diff) ?? ''
}

/**
 * Displays combined metadata values in the most important order in a single column
 */

export const combinedMetaDataValueGetter: ValueGetterFunc = (
  params: ValueGetterParams,
): string => {
  if (!params.data?.metaData) {
    return ''
  }

  // clones the metadata object and replaces it with 5 data sets in this specific order
  const sortOrder = [
    'Difference',
    'CWAN Value',
    'Client Value',
    'Predicted Value',
    'Day Difference',
  ]
  const sorted = JSON.parse(JSON.stringify(params.data.metaData, sortOrder))

  const formatted: Array<string> | unknown[] = Object.entries(sorted).map(
    ([key, value]) => {
      switch (key) {
        case 'Difference':
          return `Diff: ${fromMetaStringToNumber(calculateDifference(sorted))}`
        case 'CWAN Value':
          return `CW: ${fromMetaStringToNumber(value)}`
        case 'Client Value':
          return `Ext: ${fromMetaStringToNumber(value)}`
        case 'Predicted Value':
          return `Prediction: ${fromMetaStringToNumber(value)}`
        case 'Day Difference':
          return `Day Diff: ${fromMetaStringToNumber(value)}`
        default:
          return
      }
    },
  )
  return formatted.join('; ') ?? ''
}

/**
 * Get the issue meta data
 *
 * Logic, if the metadata is not defined, return an empty string
 * If it is defined, just stringify the JSON
 */
export const issueMetaDataValueGetter: ValueGetterFunc = (
  params: ValueGetterParams,
): string => {
  return params.data?.metaData ? JSON.stringify(params.data.metaData) : ''
}

/*
 * Format number
 */
/** params the grid params
 * precision teh digits after decimal
 *
 */
export function formatNumber(
  params: ValueFormatterParams,
  precision: number,
): string {
  return params.value
    ? Number(params.value).toLocaleString(undefined, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      })
    : params.value
}
